<template>
  <div class="body-2">
    <v-card flat tile :disabled="loading" :loading="loading"
      v-if="issue && issue.userId" class="lighten-4">
      <v-card-text>
        <h2 class="title black--text">
          <span>{{ issue.title }}</span>
          <v-btn text small icon color="accent" @click="init">
            <v-icon dark>mdi-refresh</v-icon>
          </v-btn>
        </h2>
        <p>Created: {{ issue.date | moment('Do MMM YYYY') }},
          Reporter: {{ issue.reporter ? issue.reporter.displayName : 'NA' }} </p>
        <div class="mb-5">
          <v-btn depressed small class="mr-1" @click="onEditItem">
            <v-icon x-small class="mr-1">mdi-pencil</v-icon>Edit
          </v-btn>
          <v-btn depressed small class="mr-1">
            Assign
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn depressed small class="mr-1" v-on="on">
                Priority
                <v-icon class="ml-1" x-small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, index) in priorities"
                :key="index" v-show="item.value !== issue.priority"
                @click="onPriorityChange(item)"
              >
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn depressed small class="mr-1" v-on="on">
                Status
                <v-icon class="ml-1" x-small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, index) in states"
                :key="index" v-show="item.value !== issue.status"
                @click="onStatusChange(item)"
              >
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <v-row>
          <v-col cols="7">
            <section>
              <h5 class="subtitle-2">More details</h5>
              <v-row dense>
                <v-col class="d-flex" cols="12" sm="6">
                  <v-list-item-content>Priority:</v-list-item-content>
                  <v-list-item-content class="align-end d-inline-block">
                    <issue-priority :value="issue.priority"></issue-priority>
                  </v-list-item-content>
                </v-col>
                <v-col class="d-flex" cols="12" sm="6">
                  <v-list-item-content>Status:</v-list-item-content>
                  <v-list-item-content class="align-end d-inline-block">
                    <issue-status :value="issue.status"></issue-status>
                  </v-list-item-content>
                </v-col>
              </v-row>
            </section>
            <section class="mb-3">
              <h5 class="subtitle-2">Description</h5>
              <issue-desc v-if="isEditingDesc" :value="issue.desc"
                @save="onDescChange"
                @cancel="isEditingDesc = false"></issue-desc>
              <v-list-item-content v-else class="cursor-edit"
                v-html="$sanitize(issue.desc) || 'Not yet added!'"
                @click="isEditingDesc = true" title="Click to edit description"
              ></v-list-item-content>
            </section>
            <section class="mb-3">
              <h5 class="subtitle-2 mb-3 d-flex justify-space-between">
                <span>Sub-tasks ({{ subtasks.length }})</span>
              </h5>
              <v-row no-gutters v-if="issue.status !== 'completed'">
                <v-col>
                  <v-textarea ref="taskInput" label="Enter sub-task & Press Enter"
                    v-model="subtask" :disabled="isSavingTasks" auto-grow
                    clearable filled outlined single-line rows="3"
                    @keyup.enter.native="onAddTask(subtask)"
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-list-item-content class="py-0" v-if="!subtasks.length">
                <div >No sub-tasks added!</div>
              </v-list-item-content>
              <v-card v-else flat>
                <div class="d-flex">
                  <v-btn-toggle dense class="mb-3" :disabled="isSavingTasks"
                    borderless mandatory v-model="selectedTab">
                    <v-btn color="accent" text small :value="filter.value"
                      v-for="(filter, index) in filterTabs" :key="index">
                      {{ filter.text }}
                    </v-btn>
                  </v-btn-toggle>
                  <v-spacer></v-spacer>
                  <issue-stats :tasks="subtasks"></issue-stats>
                </div>
                <v-card tile class="elevation-1" :loading="isSavingTasks">
                  <v-card-text v-if="sortedTasks.length === 0">No items!</v-card-text>
                  <v-simple-table v-else>
                    <template v-slot:default>
                      <tbody>
                        <tr :class="{'grey lighten-4 grey--text': item.completed}"
                          v-for="(item, index) in sortedTasks" :key="index">
                          <td class="py-2" v-text="item.title"></td>
                          <td style="width: 120px" class="text-right no-wrap">
                            <div v-if="item.completed">
                              <v-btn icon small @click="onDeleteTask(item)">
                                <v-icon color="red lighten-1">mdi-close</v-icon>
                              </v-btn>
                              <v-btn icon small @click="onUpdateTask(item)">
                                <v-icon color="accent lighten-1">mdi-undo</v-icon>
                              </v-btn>
                            </div>
                            <div v-else>
                              <v-btn icon small @click="onDeleteTask(item)">
                                <v-icon color="red lighten-1">mdi-close</v-icon>
                              </v-btn>
                              <v-btn icon small @click="onUpdateTask(item)">
                                <v-icon color="green accent-3">mdi-check</v-icon>
                              </v-btn>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card>
              </v-card>
            </section>
          </v-col>
          <v-col>
            <section class="mb-3">
              <h5 class="subtitle-2">Collaborators</h5>
              <user-info title="Reporter" :user="issue.reporter"></user-info>
              <user-info title="Assignee" :user="issue.assignee"></user-info>
            </section>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card class="py-5" flat v-else>
      <p v-if="!loading">
        Either node does not exists or invalid.
        <router-link :to="{ name: 'projects'}">View all projects</router-link>
      </p>
    </v-card>
  </div>
</template>

<style lang="css" scoped>
h5 {
  color: #000;
}
</style>

<script>
import { mapActions, mapState } from 'vuex';
import { GlobalEvents } from '@/event-bus';
import types, { BUS } from '@/store/types';

export default {
  name: 'project-task-page',
  props: ['project'],
  components: {},
  data: () => ({
    title: 'Tasks',
    results: [],
    issue: null,
    formData: undefined,
    loading: false,
    isEditingDesc: false,
    isSavingTasks: false,
    selectedTab: 'all',
    filterTabs: [
      {
        text: 'All',
        value: 'all',
      },
      {
        text: 'Pending',
        value: false,
      },
      {
        text: 'Completed',
        value: true,
      },
    ],
    subtask: '',
  }),
  computed: {
    ...mapState([
      'states',
      'priorities',
    ]),
    projectId() { return this.$route.params.projectId; },
    issueId() { return this.$route.params.issueId; },
    extraData() {
      return {
        projectId: this.projectId,
        projectName: this.project.name,
      };
    },
    subtasks() {
      if (!this.issue.subtasks || Array.isArray(this.issue.subtasks) === false) {
        return [];
      }
      return this.issue.subtasks;
    },
    sortedTasks() {
      return this.selectedTab === 'all' ? this.subtasks : this.subtasks.filter(i => i.completed === this.selectedTab);
    },
  },
  methods: {
    ...mapActions('db', {
      getIssue: types.GET_ISSUE_BY_ID,
      updateTask: types.UPDATE_ISSUE,
    }),
    getFieldSettings(list, field) {
      if (!this[list]) { return {}; }
      return this[list].find(i => i.value === field);
    },
    updateUI(payload) {
      this.issue = {
        ...this.issue,
        ...payload,
      };
    },
    updateField(value, label) {
      if (!label) {
        return;
      }
      const payload = {
        id: this.issueId,
        [label]: value,
      };
      // eslint-disable-next-line consistent-return
      return this.updateTask(payload)
        .then(() => {
          this.$emit('taskUpdated', payload);
          this.updateUI(payload);
        });
    },
    onPriorityChange(item) {
      this.updateField(item.value, 'priority');
    },
    onStatusChange(item) {
      this.updateField(item.value, 'status');
    },
    onDescChange(item) {
      this.updateField(item, 'desc').then(() => {
        this.isEditingDesc = false;
      });
    },
    onAddTask(val) {
      if (!val || !val.trim()) {
        return;
      }
      if (!this.subtasks) {
        this.subtasks = [];
      }
      this.subtasks.unshift({
        title: val.trim(),
        completed: false,
      });
      this.isSavingTasks = true;
      this.updateField(this.subtasks, 'subtasks').then(() => {
        this.isSavingTasks = false;
        this.subtask = null;
        setTimeout(() => {
          this.$refs.taskInput.focus();
        }, 200);
      });
    },
    onDeleteTask(item) {
      const results = this.subtasks.filter(task => item.title !== task.title);
      this.isSavingTasks = true;
      this.updateField(results, 'subtasks').then(() => {
        this.isSavingTasks = false;
      });
    },
    onUpdateTask(item) {
      const results = this.subtasks.map((task) => {
        const modTask = {
          ...task,
        };
        if (modTask.title === item.title) {
          modTask.completed = !modTask.completed;
        }
        return modTask;
      });
      this.isSavingTasks = true;
      this.updateField(results, 'subtasks').then(() => {
        this.isSavingTasks = false;
      });
    },
    onEditItem() {
      GlobalEvents.$emit(BUS.OPEN_MANAGE_ISSUE, {
        formData: {
          ...this.issue,
        },
        extraData: {
          ...this.extraData,
        },
      });
    },
    loadSettings() {
      this.loading = true;
      this.getIssue({
        id: this.issueId,
      }).then((res) => {
        this.issue = res ? {
          ...res,
        } : null;
      }).catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    init() {
      this.loadSettings();
    },
  },
  created() {
    this.init();
  },

  beforeDestroy() {
  },
};
</script>
